/* eslint-disable no-unused-vars */
'use client';
import React, { useState, useEffect } from 'react';
import './TeacherSidebar.css';
import IconButton from '@mui/material/IconButton';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import SidebarLogoImage from '../../assets/SidebarIcon/sidebar-logo-icon.svg';
import LogoImage from '../../assets/NavbarIcon/logo-image.png';
import { useOpen } from '../../context/OpenContext';
import { RiHome5Line } from 'react-icons/ri';
import { GoClock } from 'react-icons/go';
import { LuCalendarDays } from 'react-icons/lu';
import { IoChatboxEllipsesOutline } from 'react-icons/io5';
import { IoPersonOutline } from 'react-icons/io5';
import { IoStarOutline } from 'react-icons/io5';
import { IoWalletOutline } from 'react-icons/io5';
import { IoLogOutOutline } from 'react-icons/io5';
import { useTranslation } from '../../i18n/LanguageContext';
import LogoIcon from '../../assets/LoginIcon/Logo.png';
import axios from 'axios';
import Cookies from 'js-cookie';

const TeacherSidebar = () => {
  const [open, setOpen] = useOpen();
  const navigate = useNavigate();
  const location = useLocation();
  const { translate } = useTranslation();
  const SERVER_BASE_URL = process.env.REACT_APP_SERVER_API_URL;

  // State for user permissions
  const [userPermissions, setUserPermissions] = useState({
    can_use_wallet: true,
    can_use_chat: true,
    can_use_bookings: true,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user permissions once on component mount
  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        const accessToken = Cookies.get('access_token');
        if (!accessToken) {
          setIsLoading(false);
          return;
        }

        const { data } = await axios.get(`${SERVER_BASE_URL}/auth/users/me`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        setUserPermissions({
          can_use_wallet: data.can_use_wallet || true,
          can_use_chat: data.can_use_chat || true,
          can_use_bookings: data.can_use_bookings || true,
        });
      } catch (error) {
        console.error('Error fetching user permissions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserPermissions();
  }, [SERVER_BASE_URL]);

  // Define route mappings for nested paths
  const routeMappings = {
    // Dashboard routes
    '/instructor/dashboard': ['/instructor/dashboard'],

    // Sessions routes
    '/instructor/sessions': [
      '/instructor/sessions',
      '/instructor/sessions-details/:id',
      '/instructor/sessions-details/:sessionId',
    ],

    // Schedule routes
    '/instructor/schedule': ['/instructor/schedule'],

    // Chat routes
    '/instructor/chat': ['/instructor/chat'],

    // Wallet routes
    '/instructor/account/wallet': ['/instructor/account/wallet', '/instructor/wallet'],

    // My Account routes
    '/instructor/account': [
      '/instructor/account',
      '/instructor/account/edit-specialisation',
      '/instructor/account/reschedule-request',
      '/instructor/account/reschedule-request-detail/:id',
      '/instructor/account/reschedule-request-detail',
    ],
  };

  const getLinkClassName = path => {
    // Get all related paths for this link
    const relatedPaths = routeMappings[path] || [path];

    // Check if the current path exactly matches one of the related paths
    // or matches a pattern like '/instructor/sessions-details/123'
    const isActive = relatedPaths.some(routePath => {
      if (routePath.includes(':')) {
        // For parameterized routes, convert :id or :sessionId to a regex pattern
        const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(location.pathname);
      }
      return location.pathname === routePath;
    });

    return isActive ? 'single-nav-link active' : 'single-nav-link';
  };

  const handleLogout = async () => {
    try {
      const { data } = await axios.post(`${SERVER_BASE_URL}/auth/jwt/logout`);
      if (data) {
        Cookies.remove('access_token');
        Cookies.remove('refresh_token');
        Cookies.remove('user_type');
        Cookies.remove('first_name');
        Cookies.remove('last_name');
        Cookies.remove('email');
        Cookies.remove('profile_image');

        navigate('/');
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box
      className={`admin-sidebar-container ${open ? 'open' : 'closed'}`}
      data-testid="admin-sidebar"
    >
      <Box className="d-block px-4 pt-3 pb-4 mb-2 ">
        <IconButton
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
              cursor: 'default',
            },
            padding: 0,
          }}
          data-testid="sidebar-logo-button"
          disableRipple
        >
          <div className="admin-sidebar-logo-container">
            {open ? (
              <Box className="h-[18px] ml-[7px]">
                <img src={LogoIcon} alt="sidebar-logo-image" data-testid="sidebar-logo" />{' '}
              </Box>
            ) : (
              <Box className="w-[18px] h-[18px] ml-[7px]">
                <img src={LogoImage} alt="logo-image" data-testid="sidebar-logo" />
              </Box>
            )}
          </div>
        </IconButton>
      </Box>
      <ul
        className="nav nav-pills nav-flush flex-column mb-auto"
        style={{
          maxHeight: '80vh',
          overflowX: 'hidden',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '3px',
        }}
      >
        <li className="single-nav-item">
          <Link to="/instructor/dashboard" className={getLinkClassName('/instructor/dashboard')}>
            <div className="single-nav-link">
              <RiHome5Line className="nav-icon" />
              {open && <span>{translate('sidebar', 'dashboardText')}</span>}
            </div>
          </Link>
        </li>
        <li className="single-nav-item">
          <Link to="/instructor/sessions" className={getLinkClassName('/instructor/sessions')}>
            <div className="single-nav-link">
              <GoClock className="nav-icon" />
              {open && <span>{translate('sidebar', 'sessionsText')}</span>}
            </div>
          </Link>
        </li>
        {!isLoading && userPermissions.can_use_bookings && (
          <li className="single-nav-item">
            <Link to="/instructor/schedule" className={getLinkClassName('/instructor/schedule')}>
              <div className="single-nav-link">
                <LuCalendarDays className="nav-icon" />
                {open && <span>{translate('sidebar', 'scheduleText')}</span>}
              </div>
            </Link>
          </li>
        )}
        {!isLoading && userPermissions.can_use_chat && (
          <li className="single-nav-item">
            <Link to="/instructor/chat" className={getLinkClassName('/instructor/chat')}>
              <div className="single-nav-link">
                <IoChatboxEllipsesOutline className="nav-icon" />
                {open && <span>{translate('sidebar', 'chatText')}</span>}
              </div>
            </Link>
          </li>
        )}
        <li className="single-nav-item">
          <Link to="/instructor/reviews" className={getLinkClassName('/instructor/reviews')}>
            <div className="single-nav-link">
              <IoStarOutline className="nav-icon" />
              {open && <span>{translate('sidebar', 'reviewsText') || 'Reviews'}</span>}
            </div>
          </Link>
        </li>
        {!isLoading && userPermissions.can_use_wallet && (
          <li className="single-nav-item">
            <Link
              to="/instructor/account/wallet"
              className={getLinkClassName('/instructor/account/wallet')}
            >
              <div className="single-nav-link">
                <IoWalletOutline className="nav-icon" />
                {open && <span>{translate('sidebar', 'walletText') || 'Wallet'}</span>}
              </div>
            </Link>
          </li>
        )}
        <li className="single-nav-item">
          <Link to="/instructor/account" className={getLinkClassName('/instructor/account')}>
            <div className="single-nav-link">
              <IoPersonOutline className="nav-icon" />
              {open && <span>{translate('sidebar', 'myAccountText')}</span>}
            </div>
          </Link>
        </li>
      </ul>

      <div className="sidebar-footer mt-auto mb-4">
        <li className="single-nav-item sidebar-logout">
          <div className="single-nav-link cursor-pointer" onClick={handleLogout}>
            <IoLogOutOutline className="nav-icon" />
            {open && <span>{translate('sidebar', 'logoutText') || 'Logout'}</span>}
          </div>
        </li>
      </div>
    </Box>
  );
};

export default TeacherSidebar;
